import { FC, Fragment, lazy, useEffect } from 'react';
import { Drawer, List, Box, Theme, ThemeProvider, StyledEngineProvider, useTheme, Container } from '@mui/material';
import { BusinessCenter, AccountTreeOutlined } from '@mui/icons-material';
import { DeloitteListItemLink } from 'TaxAnalyticsUI/Deloitte';
import { LightTheme } from '../../../themes';
import { Home, Prestation, Entity, History as HistoryIcon, Settings as AdminIcon } from 'TaxAnalyticsUI/icons';
import { Route, Switch, useRouteMatch, Redirect } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { userState } from 'TaxAnalyticsState';
import { ApiClient } from 'TaxAnalyticsHelpers';
import { entitiesState } from 'TaxAnalyticsState/orderState';

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { UserRole } from 'TaxAnalyticsHelpers/TaxAnalyticsClient';
import Grid from '@mui/material/Unstable_Grid2';
import LazyLoader from 'TaxAnalyticsHelpers/LazyLoader';

const Admin = lazy(() => import('./pages/admin/Admin')
    .then(({ Admin }) => ({ default: Admin })),);
const Analytics = lazy(() => import('./pages/analytics/Analytics')
    .then(({ Analytics }) => ({ default: Analytics })),);
const History = lazy(() => import('./pages/history/History')
    .then(({ History }) => ({ default: History })),);
const Services = lazy(() => import('./pages/services/Services')
    .then(({ Services }) => ({ default: Services })),);
const Entities = lazy(() => import('./pages/entities/Entities')
    .then(({ Entities }) => ({ default: Entities })),);
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard')
    .then(({ Dashboard }) => ({ default: Dashboard })),);
const BNC = lazy(() => import('./pages/bnc/BNC')
    .then(({ BNC }) => ({ default: BNC })),);
const BNCDetail = lazy(() => import('./pages/bnc/BNCDetail')
    .then(({ BNCDetail }) => ({ default: BNCDetail })),);
const BNCInvoiceDetail = lazy(() => import('./pages/bnc/BNCInvoiceDetail')
    .then(({ BNCInvoiceDetail }) => ({ default: BNCInvoiceDetail })),);

const PTwo = lazy(() => import('./pages/p2/PTwo')
    .then(({ PTwo }) => ({ default: PTwo })),);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        drawer: {
            width: theme.deloitte.menuSize,
            flexShrink: 0
        },
        docked: {
            [theme.breakpoints.up('md')]: {
                width: 0
            },
            [theme.breakpoints.down('lg')]: {
                width: '100%',
                height: theme.deloitte.menuSize
            }
        },
        drawerPaper: {
            top: theme.deloitte.menuSize,
            [theme.breakpoints.up('md')]: {
                width: theme.deloitte.menuSize
            },
            [theme.breakpoints.down('lg')]: {
                width: '100%',
                height: theme.deloitte.menuSize + 1,
            },
            backgroundColor: '#393C48'
        },
        drawerContainer: {
            overflow: 'auto',
            [theme.breakpoints.down('lg')]: {
                height: theme.deloitte.menuSize
            }
        },
        autoList: {
            [theme.breakpoints.down('lg')]: {
                display: 'flex',
                flexDirection: 'row',
                padding: 0,
                width: '100%',
                height: theme.deloitte.menuSize
            }
        },
        fullHeight: {
            height: '100%',
            display: 'flex',
            [theme.breakpoints.down('lg')]: {
                flexDirection: 'column',
            }
        },
        subPage: {
            maxHeight: `calc(100vh - ${theme.deloitte.menuSize}px)`,
            [theme.breakpoints.up('md')]: {
                marginLeft: theme.deloitte.menuSize
            },
            [theme.breakpoints.down('lg')]: {
                marginLeft: 0,
                maxHeight: `calc(100vh - ${theme.deloitte.menuSize * 2}px)`,
            }
        }
    })
);

const Workspace: FC = (props: any) => {
    const classes = useStyles();
    const { path } = useRouteMatch();
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down('md'));;
    const anchor = small ? 'top' : 'left';
    const user = useRecoilValue(userState);
    const setEntitiesState = useSetRecoilState(entitiesState);
    const fetchAll = async () => {
        if (user && user.isAuthenticated && user.user) {
            const entities = await ApiClient.entityClient.all();
            setEntitiesState(entities);
        }
    }

    useEffect(() => {
        fetchAll();
    }, [user]);

    return (
        <Container maxWidth={false} disableGutters sx={{ backgroundColor: theme.palette.background.default }}>
            <Box flexGrow={1} flexDirection="row" className={classes.fullHeight} sx={{ width: "100vw" }}>
                <Drawer
                    className={classes.drawer}
                    variant="permanent"
                    anchor={anchor}
                    classes={{
                        paper: classes.drawerPaper,
                        docked: classes.docked
                    }}
                >
                    <div className={classes.drawerContainer}>
                        <List disablePadding className={classes.autoList}>
                            {user.user?.role !== UserRole.HR &&
                                <Fragment>
                                    <DeloitteListItemLink icon={Home} to="/workspace/dashboard" />
                                    <DeloitteListItemLink icon={Prestation} to="/workspace/services" />
                                    <DeloitteListItemLink icon={Entity} to="/workspace/entities" />
                                    <DeloitteListItemLink icon={HistoryIcon} to="/workspace/history" />

                                </Fragment>
                            }
                            {(user.user?.role === UserRole.SuperAdmin || user.user?.role === UserRole.HR) &&
                                <DeloitteListItemLink icon={BusinessCenter} to="/workspace/bnc" />
                            }
                            {(user.user?.features?.find(f => f.code === "P2") || user.user?.isAdmin) &&
                                <Fragment>
                                    <DeloitteListItemLink icon={AccountTreeOutlined} to="/workspace/p2" />
                                </Fragment>
                            }
                            {user.user?.isAdmin &&
                                <Fragment>
                                    <DeloitteListItemLink icon={AdminIcon} to="/workspace/admin" />
                                </Fragment>
                            }
                        </List>
                    </div>
                </Drawer>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={LightTheme}>
                        <Grid container flexGrow={1} id="SubPage" bgcolor="background.default" direction="row" alignItems="stretch" className={classes.subPage}>
                            <Grid id="theContainer" justifyContent="space-around" flexGrow={1} sx={{ height: 1, maxHeight: 1 }}>
                                <Box p={2} flexGrow={1} sx={{ height: 1, maxHeight: 1, overflow: 'auto' }}>
                                    {user && user.isAuthenticated &&
                                        <Switch>
                                            {(!user.user?.isHR || user.user?.isAdmin) &&
                                                <Route exact path={`${path}`}>
                                                    <Redirect to={`${path}/dashboard`} />
                                                </Route>
                                            }
                                            <Route path={`${path}/dashboard`} component={LazyLoader(Dashboard)} />
                                            <Route path={`${path}/entities/:id?`} exact component={LazyLoader(Entities)} />
                                            <Route path={`${path}/entities/:id/order/:orderId`} component={LazyLoader(Entities)} />
                                            <Route path={`${path}/services`} component={LazyLoader(Services)} />
                                            <Route path={`${path}/analytics/:id`} component={LazyLoader(Analytics)} />
                                            <Route path={`${path}/history/:id?`} component={LazyLoader(History)} />
                                            { (user.user?.features?.find(f => f.code === "P2") || user.user?.isAdmin) &&
                                                <Route path={`${path}/p2/:id?`} component={LazyLoader(PTwo)} />
                                            }
                                            {user.user?.isAdmin &&
                                                <Route path={`${path}/admin/:menu?/:id?`} component={LazyLoader(Admin)} />
                                            }
                                            {!user.user?.isAdmin && !user.user?.isHR &&
                                                <Route exact path={`${path}`}>
                                                    <Redirect to={`${path}/bnc`} />
                                                </Route>
                                            }
                                            <Route path={`${path}/bnc`} exact component={LazyLoader(BNC)} />
                                            <Route path={`${path}/bnc/:emplid`} exact component={LazyLoader(BNCDetail)} />
                                            <Route path={`${path}/bnc/:emplid/:reference`} exact component={LazyLoader(BNCInvoiceDetail)} />
                                        </Switch>
                                    }
                                </Box>
                            </Grid>
                        </Grid>
                    </ThemeProvider>
                </StyledEngineProvider>
            </Box >
        </Container >
    );
}

export default Workspace;